import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Storefront, ArrowRight, MapPin, Buildings, CurrencyDollar, FunnelSimple } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const COUNTRIES = ["Canada", "UAE", "Saudi Arabia", "Indonesia", "India", "Argentina"];

export default function FranchiseOpportunities() {
  const [params, setParams] = useSearchParams();
  const country = params.get("country") || "";
  const industry = params.get("industry") || "";
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (country) qs.append("country", country);
    if (industry) qs.append("industry", industry);
    api.get(`/franchise/opportunities?${qs.toString()}`)
      .then((r) => setOpps(r.data))
      .finally(() => setLoading(false));
  }, [country, industry]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  const industries = Array.from(new Set(opps.map((o) => o.industry).filter(Boolean)));

  return (
    <div data-testid="franchise-opps-page" className="fade-in">
      <section className="py-20 border-b border-gos-border" style={{ background: "var(--gos-bone)" }}>
        <div className="container-gos">
          <div className="overline mb-3 flex items-center gap-2">
            <Storefront size={14} weight="duotone" /> Franchise Opportunities
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-gos-charcoal max-w-3xl">
            Discover franchise opportunities <span className="text-gos-navy">across our global network.</span>
          </h1>
          <p className="text-gos-slate mt-5 max-w-2xl">
            Filter by country or industry to find an opportunity that matches your investment capacity, experience, and target market.
          </p>
          <Link to="/franchise" data-testid="opps-apply-cta" className="btn-primary mt-6">
            Apply for a franchise <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </section>

      <section className="py-12">
        <div className="container-gos">
          <div className="bg-white border border-gos-border rounded-sm p-4 mb-8 flex flex-wrap gap-3 items-center" data-testid="opps-filters">
            <FunnelSimple size={16} className="text-gos-slate" />
            <span className="overline">Country</span>
            <button
              onClick={() => setFilter("country", "")}
              data-testid="opps-filter-country-all"
              className={`px-3 py-1 text-xs uppercase tracking-widest rounded-sm border ${!country ? "bg-gos-navy text-white border-gos-navy" : "border-gos-border text-gos-slate hover:border-gos-navy"}`}
            >All</button>
            {COUNTRIES.map((c) => (
              <button
                key={c} onClick={() => setFilter("country", c)}
                data-testid={`opps-filter-country-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3 py-1 text-xs uppercase tracking-widest rounded-sm border ${country === c ? "bg-gos-navy text-white border-gos-navy" : "border-gos-border text-gos-slate hover:border-gos-navy"}`}
              >{c}</button>
            ))}
            {industries.length > 0 && (
              <>
                <span className="overline ml-4">Industry</span>
                <button onClick={() => setFilter("industry", "")} className={`px-3 py-1 text-xs uppercase tracking-widest rounded-sm border ${!industry ? "bg-gos-navy text-white border-gos-navy" : "border-gos-border text-gos-slate"}`}>All</button>
                {industries.map((i) => (
                  <button key={i} onClick={() => setFilter("industry", i)} className={`px-3 py-1 text-xs uppercase tracking-widest rounded-sm border ${industry === i ? "bg-gos-navy text-white border-gos-navy" : "border-gos-border text-gos-slate"}`}>{i}</button>
                ))}
              </>
            )}
          </div>

          {loading && <div className="text-gos-muted">Loading opportunities…</div>}
          {!loading && opps.length === 0 && (
            <div className="bg-white border border-gos-border rounded-sm p-10 text-center" data-testid="opps-empty">
              <p className="text-gos-slate">No opportunities match these filters yet.</p>
              <Link to="/franchise" className="btn-secondary mt-4 inline-flex">Submit an application instead</Link>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {opps.map((o) => (
              <Link
                key={o.id} to={`/franchise/opportunities/${o.slug}`}
                data-testid={`opp-card-${o.slug}`}
                className="bg-white border border-gos-border rounded-sm overflow-hidden hover:border-gos-navy transition-colors group"
              >
                {o.cover_image_url ? (
                  <div className="h-40 bg-gos-subtle overflow-hidden">
                    <img src={o.cover_image_url} alt={o.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  </div>
                ) : (
                  <div className="h-40 bg-gradient-to-br from-gos-navy/5 to-gos-navy/20 flex items-center justify-center text-gos-navy">
                    <Storefront size={40} weight="duotone" />
                  </div>
                )}
                <div className="p-5">
                  <div className="overline mb-1">{o.industry || "Franchise"}</div>
                  <h3 className="font-display text-xl text-gos-charcoal">{o.name}</h3>
                  {o.tagline && <p className="text-sm text-gos-slate mt-1 line-clamp-2">{o.tagline}</p>}
                  <div className="mt-4 space-y-1 text-xs text-gos-slate">
                    {(o.investment_min || o.investment_max) > 0 && (
                      <div className="flex items-center gap-2">
                        <CurrencyDollar size={14} weight="duotone" className="text-gos-navy" />
                        {o.currency} {Number(o.investment_min).toLocaleString()} – {Number(o.investment_max).toLocaleString()}
                      </div>
                    )}
                    {o.countries?.length > 0 && (
                      <div className="flex items-center gap-2"><MapPin size={14} weight="duotone" className="text-gos-navy" />{o.countries.join(" · ")}</div>
                    )}
                    {o.units_operating > 0 && (
                      <div className="flex items-center gap-2"><Buildings size={14} weight="duotone" className="text-gos-navy" />{o.units_operating} units</div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
